import { ReactNode, useEffect, useState } from "react";

import JobLogStore from "~/stores/JobLogStore";

import Icon from "~/components/shared/Icon";
import AuthWarning from "~/components/job/Job/Log/AuthWarning";
import DockerRateLimitedWarning from "~/components/job/Job/Log/DockerRateLimitedWarning";
import GitCommitNotFoundWarning from "~/components/job/Job/Log/GitCommitNotFoundWarning";
import SecretsWarning from "~/components/job/Job/Log/SecretsWarning";
import { useJobLogWarningsStore } from "~/components/job/Job/useJobLogWarningsStore";

type Props = {
  job: {
    id: string;
    state: string;
    passed: boolean | null | undefined;
    projectProviderId?: string;
    agentOsId?: string | null;
    cluster?: {
      id: string;
    };
    clusterQueue?: {
      hosted?: boolean;
    };
  };
  build: {
    branchName: string;
    commitId: string;
    account?: {
      slug: string;
    };
  };
};

type Flags = {
  sshKeyFailure: boolean;
  httpsAuthFailure: boolean;
  dockerRateLimited: boolean;
  gitCommitNotFoundInBranchFailure: boolean;
  secretsAccessFailure: boolean;
};

const readFlags = (jobId: string): Flags => {
  const log = JobLogStore.get(jobId);
  return {
    sshKeyFailure: Boolean(log?.sshKeyFailure),
    httpsAuthFailure: Boolean(log?.httpsAuthFailure),
    dockerRateLimited: Boolean(log?.dockerRateLimited),
    gitCommitNotFoundInBranchFailure: Boolean(log?.gitCommitNotFoundInBranchFailure),
    secretsAccessFailure: Boolean(log?.secretsAccessFailure),
  };
};

type DismissibleWarningProps = {
  children: ReactNode;
  onDismiss: () => void;
};

function DismissibleWarning({ children, onDismiss }: DismissibleWarningProps) {
  return (
    <div className="relative [&>.alert]:pr-10">
      {children}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss warning"
        className="hover-color-inherit absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded text-gray-700 hover:bg-black/5"
      >
        <Icon icon="close" className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function LogWarnings({ job, build }: Props) {
  const [flags, setFlags] = useState<Flags>(() => readFlags(job.id));
  const dismissedForJob = useJobLogWarningsStore((state) => state.dismissed[job.id] ?? {});
  const dismiss = useJobLogWarningsStore((state) => state.dismiss);

  useEffect(() => {
    const handler = () => setFlags(readFlags(job.id));
    handler();
    JobLogStore.addChangeListener(job.id, handler);
    return () => {
      JobLogStore.removeChangeListener(job.id, handler);
    };
  }, [job.id]);

  const isFailedFinishedJob = job.state === "finished" && !job.passed;
  const showAuthWarning =
    (flags.sshKeyFailure || flags.httpsAuthFailure) &&
    isFailedFinishedJob &&
    !!job.projectProviderId;
  const showDockerRateLimited = flags.dockerRateLimited && isFailedFinishedJob;
  const showGitCommitNotFound = flags.gitCommitNotFoundInBranchFailure && isFailedFinishedJob;
  const showSecretsWarning = flags.secretsAccessFailure;

  const dismissHandler = (key: string) => () => dismiss(job.id, key);

  return (
    <>
      {showAuthWarning && job.projectProviderId && !dismissedForJob.auth && (
        <DismissibleWarning onDismiss={dismissHandler("auth")}>
          <AuthWarning
            repositoryProviderId={job.projectProviderId}
            osId={job.agentOsId}
            hostedQueue={job.clusterQueue?.hosted}
          />
        </DismissibleWarning>
      )}
      {showDockerRateLimited && !dismissedForJob.docker && (
        <DismissibleWarning onDismiss={dismissHandler("docker")}>
          <DockerRateLimitedWarning />
        </DismissibleWarning>
      )}
      {showSecretsWarning && !dismissedForJob.secrets && (
        <DismissibleWarning onDismiss={dismissHandler("secrets")}>
          <SecretsWarning organizationSlug={build.account?.slug} clusterId={job.cluster?.id} />
        </DismissibleWarning>
      )}
      {showGitCommitNotFound && !dismissedForJob.gitCommit && (
        <DismissibleWarning onDismiss={dismissHandler("gitCommit")}>
          <GitCommitNotFoundWarning branch={build.branchName} commit={build.commitId} />
        </DismissibleWarning>
      )}
    </>
  );
}
